import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  HiOutlineMagnifyingGlass,
  HiOutlineMusicalNote,
  HiOutlineGlobeAlt,
  HiOutlineArrowTopRightOnSquare,
  HiOutlineDocumentText,
  HiOutlineSparkles,
  HiOutlineEye,
} from "react-icons/hi2";
import { FaYoutube } from "react-icons/fa";
import {
  browseCifraArtistSongs,
  CifraChordVersion,
  CifraImportPrefill,
  CifraSong,
  fetchCifraImportData,
  searchCifraSongs,
} from "../utils/cifraApi";
import CifraPreviewModal from "./CifraPreviewModal";

interface CifraImportPanelProps {
  /** Prefill the search field (e.g. from library search with no results). */
  initialQuery?: string;
  /** Auto-run search when initialQuery is provided. */
  autoSearch?: boolean;
  onImportReady: (prefill: CifraImportPrefill, notice?: string) => void;
  compact?: boolean;
  /** Hide the Original / Simplified hint row (e.g. when the page already explains it). */
  hideLegend?: boolean;
}

const CifraImportPanel: React.FC<CifraImportPanelProps> = ({
  initialQuery = "",
  autoSearch = false,
  onImportReady,
  compact = false,
  hideLegend = false,
}) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState(initialQuery);
  const [artistQuery, setArtistQuery] = useState("");
  const [browseMode, setBrowseMode] = useState(false);
  const [songs, setSongs] = useState<CifraSong[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fetchingChords, setFetchingChords] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [previewSong, setPreviewSong] = useState<CifraSong | null>(null);
  const [previewVersion, setPreviewVersion] =
    useState<CifraChordVersion>("principal");

  const openPreview = (
    song: CifraSong,
    version: CifraChordVersion = "principal",
  ) => {
    setPreviewSong(song);
    setPreviewVersion(version);
  };

  useEffect(() => {
    setQuery(initialQuery);
  }, [initialQuery]);

  useEffect(() => {
    if (!autoSearch || !initialQuery.trim()) return;
    void runSearch(initialQuery.trim());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSearch, initialQuery]);

  const runSearch = async (q: string) => {
    setLoading(true);
    setError("");
    setSongs([]);
    setHasSearched(true);
    try {
      const results = await searchCifraSongs(q);
      setSongs(results);
      if (!results.length) {
        setError(t("noSongsFound") || "No songs found");
      }
    } catch (err: any) {
      setError(err.response?.data?.error || t("searchFailed") || "Search failed");
      setSongs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;
    await runSearch(q);
  };

  const handleBrowseArtist = async (e: React.FormEvent) => {
    e.preventDefault();
    const artist = artistQuery.trim();
    if (!artist) return;
    setLoading(true);
    setError("");
    setSongs([]);
    setHasSearched(true);
    try {
      const results = await browseCifraArtistSongs(artist);
      setSongs(results);
      if (!results.length) {
        setError(t("noSongsFound") || "No songs found");
      }
    } catch (err: any) {
      setError(
        err.response?.data?.error ||
          t("failedLoadArtistSongs") ||
          "Failed to load artist songs",
      );
      setSongs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToLibrary = async (
    song: CifraSong,
    version: "principal" | "simplificada",
  ) => {
    setFetchingChords(`${song.songSlug}-${version}`);
    try {
      const { prefill, notice } = await fetchCifraImportData(song, version);
      onImportReady(prefill, notice);
    } finally {
      setFetchingChords(null);
    }
  };

  const songKey = (s: CifraSong) => `${s.artistSlug}-${s.songSlug}`;
  const isFetching = fetchingChords !== null;

  return (
    <div className={compact ? "space-y-3" : "space-y-4"}>
      {/* Mode toggle */}
      <div className="flex w-full sm:w-auto p-1 rounded-xl bg-gray-100/80 dark:bg-gray-900/80 border border-gray-200/60 dark:border-gray-700/60">
        <button
          type="button"
          onClick={() => setBrowseMode(false)}
          className={`flex-1 sm:flex-none px-3.5 py-2.5 sm:py-2 rounded-lg text-sm font-medium transition-colors touch-manipulation ${
            !browseMode
              ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm"
              : "text-gray-500 dark:text-gray-400"
          }`}
        >
          {t("search")}
        </button>
        <button
          type="button"
          onClick={() => setBrowseMode(true)}
          className={`flex-1 sm:flex-none px-3.5 py-2.5 sm:py-2 rounded-lg text-sm font-medium transition-colors touch-manipulation ${
            browseMode
              ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm"
              : "text-gray-500 dark:text-gray-400"
          }`}
        >
          <span className="sm:hidden">{t("byArtist") || "Artist"}</span>
          <span className="hidden sm:inline">
            {t("browseByArtist") || "Browse by artist"}
          </span>
        </button>
      </div>

      {/* Search form */}
      {browseMode ? (
        <form onSubmit={handleBrowseArtist}>
          <div className="flex gap-2">
            <div className="relative flex-1 min-w-0">
              <HiOutlineMagnifyingGlass className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
              <input
                type="text"
                placeholder={
                  t("artistBrowsePlaceholder") ||
                  "Artist name or slug (e.g. hillsong)"
                }
                value={artistQuery}
                onChange={(e) => setArtistQuery(e.target.value)}
                className="w-full h-12 sm:h-11 pl-11 pr-3 rounded-xl text-base sm:text-sm border border-gray-200/80 dark:border-gray-600/80 bg-white/80 dark:bg-gray-900/40 text-gray-800 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#5932EA]/30 focus:border-[#5932EA]"
                maxLength={100}
              />
            </div>
            <button
              type="submit"
              disabled={loading || !artistQuery.trim()}
              className="h-12 sm:h-11 px-3.5 sm:px-4 rounded-xl text-sm font-medium border border-[#5932EA]/35 dark:border-[#5932EA]/40 bg-[#5932EA]/10 dark:bg-[#5932EA]/15 text-[#5932EA] dark:text-[#c4b5fd] disabled:opacity-50 shrink-0 inline-flex items-center justify-center gap-2 transition-colors touch-manipulation"
            >
              {loading ? (
                <span className="w-4 h-4 border-2 border-[#5932EA]/25 border-t-[#5932EA] rounded-full animate-spin" />
              ) : (
                <>
                  <span className="sm:hidden">{t("load") || "Load"}</span>
                  <span className="hidden sm:inline">
                    {t("loadSongs") || "Load songs"}
                  </span>
                </>
              )}
            </button>
          </div>
        </form>
      ) : (
        <form onSubmit={handleSearch}>
          <div className="flex gap-2">
            <div className="relative flex-1 min-w-0">
              <HiOutlineMagnifyingGlass className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
              <input
                type="search"
                placeholder={
                  t("searchByNameOrArtist") || "Search by artist or song..."
                }
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="w-full h-12 sm:h-11 pl-11 pr-3 rounded-xl text-base sm:text-sm border border-gray-200/80 dark:border-gray-600/80 bg-white/80 dark:bg-gray-900/40 text-gray-800 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#5932EA]/30 focus:border-[#5932EA]"
                maxLength={150}
              />
            </div>
            <button
              type="submit"
              disabled={loading || !query.trim()}
              className="h-12 sm:h-11 px-3.5 sm:px-4 rounded-xl text-sm font-medium border border-[#5932EA]/35 dark:border-[#5932EA]/40 bg-[#5932EA]/10 dark:bg-[#5932EA]/15 text-[#5932EA] dark:text-[#c4b5fd] disabled:opacity-50 shrink-0 inline-flex items-center justify-center gap-1.5 transition-colors touch-manipulation"
            >
              {loading ? (
                <span className="w-4 h-4 border-2 border-[#5932EA]/25 border-t-[#5932EA] rounded-full animate-spin" />
              ) : (
                <>
                  <HiOutlineMagnifyingGlass className="w-4 h-4" />
                  <span className="hidden sm:inline">{t("search")}</span>
                </>
              )}
            </button>
          </div>
        </form>
      )}

      {/* Version legend — compact modal uses a short line; full page can hide when it has its own cards */}
      {!hideLegend && (
        <div
          className={`text-xs text-gray-500 dark:text-gray-400 px-0.5 ${
            compact
              ? "leading-relaxed"
              : "flex flex-col sm:flex-row gap-2 sm:gap-4"
          }`}
        >
          {compact ? (
            <p>
              <span className="text-gray-700 dark:text-gray-300 font-medium">
                {t("importOriginal") || "Original"}
              </span>
              {" · "}
              {t("importOriginalHint") || "Full chords"}
              {" · "}
              <span className="text-gray-700 dark:text-gray-300 font-medium">
                {t("importSimplified") || "Simplified"}
              </span>
              {" · "}
              {t("importSimplifiedHint") || "Easier version when available"}
            </p>
          ) : (
            <>
              <span className="inline-flex items-start gap-1.5">
                <HiOutlineDocumentText className="w-3.5 h-3.5 mt-0.5 text-[#5932EA] shrink-0" />
                <span>
                  <strong className="text-gray-700 dark:text-gray-300 font-medium">
                    {t("importOriginal") || "Original"}
                  </strong>
                  {" — "}
                  {t("importOriginalHint") || "Full chord chart from Cifra Club"}
                </span>
              </span>
              <span className="inline-flex items-start gap-1.5">
                <HiOutlineSparkles className="w-3.5 h-3.5 mt-0.5 text-amber-500 shrink-0" />
                <span>
                  <strong className="text-gray-700 dark:text-gray-300 font-medium">
                    {t("importSimplified") || "Simplified"}
                  </strong>
                  {" — "}
                  {t("importSimplifiedHint") ||
                    "Easier chord version when available"}
                </span>
              </span>
            </>
          )}
        </div>
      )}

      {error && (
        <div className="p-3 rounded-xl bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm border border-red-100 dark:border-red-900/40">
          {error}
        </div>
      )}

      {loading && (
        <ul className="rounded-2xl border border-gray-200/70 dark:border-gray-700/70 overflow-hidden divide-y divide-gray-100/90 dark:divide-gray-700/70">
          {[1, 2, 3, 4].map((i) => (
            <li key={i} className="flex gap-3 px-3.5 py-3 animate-pulse">
              <div className="w-12 h-12 rounded-xl bg-gray-200 dark:bg-gray-700 shrink-0" />
              <div className="flex-1 space-y-2 py-1">
                <div className="h-3.5 w-2/3 rounded bg-gray-200 dark:bg-gray-700" />
                <div className="h-3 w-1/3 rounded bg-gray-200 dark:bg-gray-700" />
              </div>
            </li>
          ))}
        </ul>
      )}

      {!loading && songs.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400 px-0.5 tabular-nums">
            {songs.length}{" "}
            {songs.length === 1
              ? t("result") || "result"
              : t("results") || "results"}
          </p>
          <ul className="rounded-2xl border border-gray-200/70 dark:border-gray-700/70 bg-white/50 dark:bg-gray-900/20 overflow-hidden divide-y divide-gray-100/90 dark:divide-gray-700/70">
            {songs.map((song) => {
              const fetchingOriginal =
                fetchingChords === `${song.songSlug}-principal`;
              const hasSimplified = Boolean(song.imprimirUrlSimplificada);

              return (
                <li
                  key={songKey(song)}
                  className="px-3.5 sm:px-4 py-3 hover:bg-[#5932EA]/5 dark:hover:bg-[#5932EA]/10 transition-colors"
                >
                  <div className="flex items-center gap-2.5 sm:gap-3">
                    <div className="w-12 h-12 rounded-xl overflow-hidden bg-gray-100 dark:bg-gray-700 shrink-0 flex items-center justify-center">
                      {song.artworkURL ? (
                        <img
                          src={song.artworkURL}
                          alt={song.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <HiOutlineMusicalNote className="w-6 h-6 text-gray-400" />
                      )}
                    </div>

                    <div className="min-w-0 flex-1">
                      <h3 className="font-semibold text-sm text-gray-800 dark:text-gray-100 truncate">
                        {song.title}
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                        {song.artist}
                        {song.albumName ? (
                          <span className="hidden sm:inline text-gray-400 dark:text-gray-500">
                            {" "}
                            · {song.albumName}
                          </span>
                        ) : null}
                      </p>
                      {(song.hasVideo || hasSimplified) && (
                        <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                          {song.hasVideo && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wide bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400">
                              <FaYoutube className="w-3 h-3" />
                              {t("video") || "Video"}
                            </span>
                          )}
                          {hasSimplified && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wide bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300">
                              <HiOutlineSparkles className="w-3 h-3" />
                              {t("importSimplified") || "Simplified"}
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    <a
                      href={song.cifraclubUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={t("viewOnCifraClub") || "View on Cifra Club"}
                      className="shrink-0 p-2.5 rounded-xl text-gray-400 hover:text-[#5932EA] hover:bg-[#5932EA]/10 transition-colors touch-manipulation"
                      aria-label={t("viewOnCifraClub") || "View on Cifra Club"}
                    >
                      <HiOutlineArrowTopRightOnSquare className="w-4 h-4" />
                    </a>
                  </div>

                  <div className="mt-2.5 grid grid-cols-2 sm:grid-cols-3 gap-2">
                    <button
                      type="button"
                      onClick={() => openPreview(song, "principal")}
                      disabled={isFetching}
                      className="min-h-[40px] px-2.5 py-2 rounded-xl text-xs sm:text-sm font-medium border border-[#5932EA]/35 bg-[#5932EA]/10 text-[#4c2bd4] dark:text-[#c4b5fd] hover:bg-[#5932EA]/18 disabled:opacity-50 transition-colors inline-flex items-center justify-center gap-1.5 touch-manipulation"
                    >
                      <HiOutlineEye className="w-4 h-4 shrink-0 opacity-80" />
                      {t("preview") || "Preview"}
                    </button>

                    <button
                      type="button"
                      onClick={() => handleAddToLibrary(song, "principal")}
                      disabled={isFetching}
                      className="min-h-[40px] px-2.5 py-2 rounded-xl text-xs sm:text-sm font-medium border border-gray-200/80 dark:border-gray-600/80 bg-white/70 dark:bg-gray-800/50 text-gray-700 dark:text-gray-200 hover:bg-white dark:hover:bg-gray-700 disabled:opacity-50 transition-colors inline-flex items-center justify-center gap-1.5 touch-manipulation"
                    >
                      {fetchingOriginal ? (
                        <span className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                      ) : (
                        <HiOutlineDocumentText className="w-4 h-4 shrink-0 opacity-70" />
                      )}
                      {fetchingOriginal
                        ? t("importing") || "…"
                        : t("importOriginal") || "Import"}
                    </button>

                    {hasSimplified ? (
                      <button
                        type="button"
                        onClick={() => openPreview(song, "simplificada")}
                        disabled={isFetching}
                        className="col-span-2 sm:col-span-1 min-h-[40px] px-2.5 py-2 rounded-xl text-xs sm:text-sm font-medium border border-amber-300/50 dark:border-amber-500/30 bg-amber-50/70 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200 hover:bg-amber-100/80 disabled:opacity-50 transition-colors inline-flex items-center justify-center gap-1.5 touch-manipulation"
                      >
                        <HiOutlineSparkles className="w-4 h-4 shrink-0 opacity-80" />
                        {t("previewSimplified") || "Simplified"}
                      </button>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {!loading && !error && songs.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 rounded-2xl border border-dashed border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400 text-center px-4">
          <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-[#5932EA]/10 text-[#5932EA] mb-3">
            <HiOutlineGlobeAlt className="w-7 h-7" strokeWidth={1.5} />
          </div>
          <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            {hasSearched
              ? t("noSongsFound") || "No songs found"
              : t("songImporterEmpty") || "Search Song Importer"}
          </p>
          <p className="text-xs mt-1.5 max-w-sm leading-relaxed">
            {hasSearched
              ? t("songImporterTryAgain") ||
                "Try another spelling, or browse by artist."
              : t("songImporterEmptyHint") ||
                "Find chords and YouTube from Cifra Club when a song is not in your library."}
          </p>
        </div>
      )}

      {previewSong && (
        <CifraPreviewModal
          song={previewSong}
          initialVersion={previewVersion}
          onClose={() => setPreviewSong(null)}
          onImportReady={(prefill, notice) => {
            setPreviewSong(null);
            onImportReady(prefill, notice);
          }}
        />
      )}
    </div>
  );
};

export default CifraImportPanel;
